import * as THREE from "three"
import { animateTransition } from "../helpers/Helpers"
import { isScaleBarFor, updateScaleBar } from "../helpers/ScaleBar"
import { sceneManager } from ".."

// Spawn slots alternating left and right of the centre line, row by row:
// characters fill rows going back, props fill rows in front
const spawnSlots = {}
const getSpawnSlots = subjectType => {
	const key = subjectType == "Character" ? "Character" : "Prop"
	if (!spawnSlots[key]) {
		spawnSlots[key] = []
		for (let row = 0; row < 10; row++) {
			const z = key == "Character" ? row * -70 : (row * 70) + 70
			for (const x of [0, 70, -70, 140, -140, 210, -210]) {
				spawnSlots[key].push(new THREE.Vector3(x, 0, z))
			}
		}
	}
	return spawnSlots[key]
}

const pendingProps = new Set()

// Box a subject of the given size would occupy standing on the ground at point,
// lifted slightly so floor-level objects (planes, rugs) don't count as overlapping
const spawnBox = (point, size) => new THREE.Box3(
	new THREE.Vector3(point.x - size.x / 2, 1, point.z - size.z / 2),
	new THREE.Vector3(point.x + size.x / 2, Math.max(size.y, 2), point.z + size.z / 2)
)

export default class Subject {
	constructor() {}

	addHelper() {
		const box = new THREE.Box3()
		box.setFromCenterAndSize(
			new THREE.Vector3(0, 0, 0),
			new THREE.Vector3(3, 3, 3)
		)

		const helper = new THREE.Box3Helper(box, 0xff0000)
		this.object.add(helper)
	}
    
	print() {
		console.log(this)
	}

	// First spawn slot whose footprint doesn't overlap anything already in the scene.
	// size is the new subject's bounding box size; before it has loaded, a rough default is used.
	getSpawnPoint(subjectType, size = null) {
		if (!size) size = subjectType == "Character" ? new THREE.Vector3(60, 180, 60) : new THREE.Vector3(40, 40, 40)

		const obstacles = []
		sceneManager.sceneCharacters.forEach(character => {
			if (character === this || !character.alive) return
			if (character.loading) {
				if (character.spawnPoint) obstacles.push(spawnBox(character.spawnPoint, new THREE.Vector3(60, 180, 60)))
			} else if (character.object) {
				obstacles.push(new THREE.Box3().setFromObject(character.getBoundingObject()))
			}
		})
		sceneManager.sceneProps.forEach(prop => {
			if (prop === this || !prop.alive || !prop.object) return
			obstacles.push(new THREE.Box3().setFromObject(prop.object))
		})
		// Props that are still loading aren't in sceneProps yet
		pendingProps.forEach(prop => {
			if (prop === this || !prop.alive || sceneManager.sceneProps.includes(prop)) {
				if (prop !== this) pendingProps.delete(prop)
				return
			}
			obstacles.push(spawnBox(prop.spawnPoint, new THREE.Vector3(40, 40, 40)))
		})

		const isFree = point => {
			// Small gap so new subjects don't spawn touching their neighbours
			const box = spawnBox(point, size).expandByVector(new THREE.Vector3(5, 0, 5))
			return !obstacles.some(obstacle => !obstacle.isEmpty() && obstacle.intersectsBox(box))
		}

		// Keep the point picked while loading if it's still free, so the subject doesn't jump
		let point = this.spawnPoint && isFree(this.spawnPoint) ? this.spawnPoint : null
		if (!point) {
			const slots = getSpawnSlots(subjectType)
			point = slots.find(isFree) || slots[0]
		}
		this.spawnPoint = point.clone()
		if (subjectType != "Character") pendingProps.add(this)

		return new THREE.Vector3(point.x, 70, point.z)
	}

	getPosition() {
		if (this.base) {
			return new THREE.Vector3().copy(this.base.position)
		}
	}

	setPosition(position, animate=true) {
		if (!position) {
			if (this.subjectType == "Character") {
				if (!this.info.centeredOnly) {
					const size = new THREE.Box3().setFromObject(this.getBoundingObject()).getSize(new THREE.Vector3())
					this.base.position.copy(this.getSpawnPoint(this.subjectType, size))
				}
				this.putOnGround(animate)
			} else {
				const size = new THREE.Box3().setFromObject(this.object).getSize(new THREE.Vector3())
				this.base.position.copy(this.getSpawnPoint(this.subjectType, size))
			}
		} else {
			if (animate) {
				animateTransition(this.base.position, position, tween => this.base.position.copy(tween))
			} else {
				this.base.position.copy(position)
			}
		}
	}

	getScale() {
		if (this.base) {
			return new THREE.Vector3().copy(this.base.scale)
		}
	}

	setScale(scale) {
		if (!scale) return

		animateTransition(this.base.scale, scale, tween => {
			this.base.scale.copy(tween)
			if (isScaleBarFor(this)) updateScaleBar(this)
		})
	}

	getRotation() {
        return new THREE.Vector3().copy(this.object.rotation)
	}

	setRotation(rotation) {
		if (!rotation) return
		animateTransition({
			x: this.base.rotation.x,
			y: this.base.rotation.y,
			z: this.base.rotation.z,

		},
		rotation,
		value => this.base.rotation.set(value.x, value.y, value.z))
	}

    getState() {
        const state = {
            world: {
                position: new THREE.Vector3(),
                quaternion: new THREE.Quaternion(),
                scale: new THREE.Vector3()
            },
            local: {
                position: new THREE.Vector3(),
                quaternion: new THREE.Quaternion(),
                scale: new THREE.Vector3()
            },
        }
        this.object.matrixWorld.decompose( state.world.position, state.world.quaternion, state.world.scale );
        this.base.matrixWorld.decompose( state.local.position, state.local.quaternion, state.local.scale );

        return state
    }

    setState(state) {
        if (!state) return

        this.object.position.copy(state.world.position)
        this.object.scale.copy(state.world.scale)
        this.object.applyQuaternion(state.world.quaternion)
    }
}
